import React, { useMemo, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { PeriodsProps, getPeriodStartEndDates } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FormatUTC } from 'shared/utils/formatUTC';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import YearModel from './YearModel';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  ReportsForProgram,
  getReportsForProgram,
} from '../../../ReportsBeta/NewReport/api';
import { useClient } from 'shared/apollo';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { logError } from 'shared/utils/logError';

type ShowPeriodProps = PeriodsProps

const StyledDiv = styled.div`
  .date-cards {
    display: flex;
    justify-content: center;
    gap: ${SPACERS.medium};
    .start-date,
    .end-date {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 280px;
      height: 163px;
      border-radius: 4px;
      border: 1px solid ${COLORS.grayLight};
      background-color: ${COLORS.grayLightest};
    }
    .month-text {
      margin-bottom: 0px;
      text-transform: uppercase;
      margin-top: ${SPACERS.tiny};
    }
  }
  .date-example {
    max-width: 483px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.small};
    margin-top: ${SPACERS.extraLarge};
  }
`;

const currentYear = new Date().getFullYear();
const getAllReportYears = (
  reportId: number,
  reports: ReportsForProgram[],
): {
  reports: { [year: string]: boolean | undefined };
  report: ReportsForProgram | undefined;
} => {
  // reportId might be a string, hence, == instead of ===
  const report = reports.find(x => x.reportId == reportId);
  if (!report) return { reports: {}, report: undefined };
  const result: ReturnType<typeof getAllReportYears> = { reports: {}, report };
  for (const r of reports) {
    if (r.programId === report.programId) {
      if (r.year) {
        result.reports[r.year.toString()] = true;
      } else {
        logError('Report year is undefined', r);
      }
    }
  }
  return result;
};

const ShowPeriod: React.FC<ShowPeriodProps> = ({
  startDate,
  endDate,
  periodType,
  reportYear,
  reportId,
  isThirdParty,
  setSelectedCard,
  onChangePeriod,
}) => {
  const client = useClient();
  const [showUpdateYear, setShowUpdateYear] = useState<boolean>(false);
  const [reportsForProgram, makeReportsForProgramRequest] = useGraphQLRequest<{
    reports: ReportsForProgram[];
  }>();

  const reportYears: ReturnType<typeof getAllReportYears> = useMemo(() => {
    if (reportsForProgram.type !== 'REQUEST_SUCCESS')
      return { reports: {}, report: undefined };
    return getAllReportYears(reportId, reportsForProgram.data?.reports);
  }, [reportId, reportsForProgram]);

  const handleOpen = useCallback(() => setShowUpdateYear(true), [
    setShowUpdateYear,
  ]);
  const handleClose = useCallback(() => setShowUpdateYear(false), [
    setShowUpdateYear,
  ]);

  const reloadPrograms = useCallback(() => {
    if (!client) return;
    makeReportsForProgramRequest(getReportsForProgram(client));
  }, [client]);

  useEffect(() => {
    reloadPrograms();
  }, [client]);

  useEffect(() => {
    if (!periodType) return;
    const period = getPeriodStartEndDates(
      periodType,
      startDate ? startDate.getFullYear() : Number(reportYear) || currentYear,
    );
    period && onChangePeriod(period);
  }, [periodType, onChangePeriod]);

  if (
    reportsForProgram.type === 'REQUEST_INIT' ||
    reportsForProgram.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  if (!startDate || !endDate) {
    return null;
  }

  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  const startMonth = FormatUTC.getFullMonth(startDate.getMonth());
  const endMonth = FormatUTC.getFullMonth(endDate.getMonth());

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  return (
    <>
      <StyledDiv>
        <div className="date-cards">
          <div className="start-date">
            <TrueImpactText variant="heading-label" color="blackBase">
              Start Date
            </TrueImpactText>
            <TrueImpactText variant="display-large">{startDay}</TrueImpactText>
            <TrueImpactText variant="heading" className="month-text">
              {startMonth}
            </TrueImpactText>
          </div>
          <div className="end-date">
            <TrueImpactText variant="heading-label" color="blackBase">
              End Date
            </TrueImpactText>
            <TrueImpactText variant="display-large">{endDay}</TrueImpactText>
            <TrueImpactText variant="heading" className="month-text">
              {endMonth}
            </TrueImpactText>
          </div>
        </div>
        <TrueImpactText variant="body-large" className="date-example">
          For example, the reporting period for this report will show as{' '}
          <strong>
            {startMonth} {startDay}, {startYear} to {endMonth} {endDay},{' '}
            {endYear}
          </strong>
          .
        </TrueImpactText>
        <TrueImpactText color="ctaPrimary" onClick={handleOpen}>
          Need to update the year for this report?
        </TrueImpactText>
      </StyledDiv>
      <YearModel
        open={showUpdateYear}
        isThirdParty={isThirdParty}
        selectedPeriodType={periodType || 'customYear'}
        handleClose={handleClose}
        startDate={startDate}
        endDate={endDate}
        reportId={reportId}
        report={reportYears.report}
        reportYears={reportYears.reports}
        reloadPrograms={reloadPrograms}
        onChangePeriod={onChangePeriod}
        periodType={periodType}
        setSelectedCard={setSelectedCard}
      />
    </>
  );
};

export default ShowPeriod;
